/**
 * SparkCard — hover a symbol in the screener and see its last 60 trading bars.
 *
 * Close, % change over the window, the high/low range, a line of closes and
 * volume bars beneath it (owner, 2026-10-07). Opens toward the charts, off the
 * screener's edge (`lib/spark-card.ts`). Not shown in the narrow shell, which
 * has no hover. See `docs/features/screener.md`, "The hover card".
 *
 * `useSparkHover` owns the timing and the card's state, so the hover costs the
 * card a render and not the screener: the grid re-renders on its own clock.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { themeCssVariables } from "@qufolio/model";
import { useTheme } from "./theme";
import type { SymbolHoverBox } from "@qufolio/screener-grid";
import { fetchSpark, type SparkCard as SparkData } from "../api/spark";
import { cardPosition, cardSide, linePoints } from "../lib/spark-card";

/** How long the pointer rests on a cell before the card opens. */
const OPEN_DELAY_MS = 300;
const WIDTH = 300;
const HEIGHT = 168;
const LINE_H = 72;
const VOLUME_H = 22;
const PLOT_W = WIDTH - 24;

interface Open {
  at: SymbolHoverBox;
  card: SparkData;
}

/**
 * The grid's `onHoverSymbol` and the card to render. A move to another cell
 * restarts the delay; leaving closes at once. A fetch that lands after the
 * pointer has moved on is dropped.
 */
export function useSparkHover(): {
  onHoverSymbol: (symbol: string | null, at: SymbolHoverBox | null) => void;
  card: JSX.Element | null;
} {
  const [open, setOpen] = useState<Open | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const current = useRef<string | null>(null);

  const onHoverSymbol = useCallback((symbol: string | null, at: SymbolHoverBox | null) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    current.current = symbol;
    if (!symbol || !at) {
      setOpen(null);
      return;
    }
    timer.current = setTimeout(() => {
      void fetchSpark(symbol).then((card) => {
        if (current.current === symbol && card.bars.length > 0) setOpen({ at, card });
      });
    }, OPEN_DELAY_MS);
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { onHoverSymbol, card: open ? <SparkCardView {...open} /> : null };
}

function SparkCardView({ at, card }: Open) {
  // **The theme's variables, on the card itself.** It is portalled to `body`,
  // outside the shell root that carries `--qf-*`, so without these every
  // `var(--qf-…)` below resolves to nothing and the card is see-through —
  // which is how it first shipped. IndicatorPicker's portal does the same.
  const theme = useTheme();
  const themeVars = useMemo(() => themeCssVariables(theme) as CSSProperties, [theme]);
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const side = cardSide(at.grid, viewport);
  const { left, top } = cardPosition(side, at.cell, at.grid, { width: WIDTH, height: HEIGHT }, viewport);

  const closes = card.bars.map((b) => b.close);
  const volumes = card.bars.map((b) => b.volume ?? 0);
  const maxVolume = Math.max(...volumes, 1);
  const barW = PLOT_W / Math.max(card.bars.length, 1);
  // Coloured by sign with no `+`, and the tone read off the printed digits —
  // the legend's rule, so a change that rounds to zero is neither colour.
  const pct = card.changePct == null ? "" : `${card.changePct.toFixed(2)}%`;
  const tone =
    !pct || Number(card.changePct!.toFixed(2)) === 0
      ? undefined
      : card.changePct! > 0
        ? "var(--qf-positive)"
        : "var(--qf-negative)";
  const stroke = tone ?? "var(--qf-text-secondary)";

  return createPortal(
    <div
      role="tooltip"
      style={{
        ...themeVars,
        position: "fixed",
        left,
        top,
        width: WIDTH,
        height: HEIGHT,
        boxSizing: "border-box",
        padding: "8px 12px",
        // Above the chart's axis labels, which painted over it at 1000; the
        // indicator picker's portal uses the same.
        zIndex: 10000,
        pointerEvents: "none",
        background: "var(--qf-surface)",
        color: "var(--qf-text)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,.35)",
        fontSize: "0.78rem",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <strong>{card.symbol}</strong>
        <span>{fmt(card.close)}</span>
        {pct ? <span style={{ color: tone }}>{pct}</span> : null}
        <span style={{ marginLeft: "auto", color: "var(--qf-text-muted)" }}>{card.bars.length} bars</span>
      </div>
      <svg width={PLOT_W} height={LINE_H + VOLUME_H + 4} style={{ display: "block", marginTop: 6 }}>
        <polyline
          points={linePoints(closes, PLOT_W, LINE_H)}
          // In `style`, not as attributes: an SVG presentation attribute does
          // not take `var()`, so `stroke="var(--qf-…)"` drew no line at all.
          style={{ fill: "none", stroke, strokeWidth: 1.5, strokeLinejoin: "round" }}
        />
        {volumes.map((v, i) => {
          const h = (v / maxVolume) * VOLUME_H;
          return (
            <rect
              key={i}
              x={i * barW + barW * 0.15}
              y={LINE_H + 4 + VOLUME_H - h}
              width={Math.max(barW * 0.7, 1)}
              height={h}
              style={{ fill: "var(--qf-text-muted)", opacity: 0.55 }}
            />
          );
        })}
      </svg>
      <div style={{ display: "flex", gap: 12, marginTop: 4, color: "var(--qf-text-secondary)" }}>
        <span>
          L {fmt(card.low)} – H {fmt(card.high)}
        </span>
        <span style={{ marginLeft: "auto" }}>Vol {compact(volumes[volumes.length - 1])}</span>
      </div>
    </div>,
    document.body,
  );
}

function fmt(v: number | undefined): string {
  return v === undefined || !Number.isFinite(v) ? "" : v.toFixed(2);
}

function compact(v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v);
}
